import * as React from 'react'
import { Archive, Check, FileText, Pencil, Plus, Share2, Trash2 } from 'lucide-react'
import { Link, useRouterState } from '@tanstack/react-router'
import { noteColorVars, useNotes } from '@/state/notes-context'
import { type NoteColor } from '@shared/types'
import { ColourPicker } from '@/components/colour-picker'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupAction,
  SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuAction,
  SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, SidebarSeparator, useSidebar,
} from '@/components/ui/sidebar'

const mainLinks = [
  { to: '/', label: 'All notes', icon: FileText },
  { to: '/shared', label: 'Shared', icon: Share2 },
] as const
const navButtonClass = 'sidebar-nav-button h-10 gap-3 rounded-xl px-3 text-sm [&>svg]:size-[18px]'

export function AppSidebar() {
  const { labels, notes, addLabel, renameLabel, removeLabel } = useNotes()
  const { setOpenMobile } = useSidebar()
  const [labelDialog, setLabelDialog] = React.useState<string | null>(null)
  const [labelName, setLabelName] = React.useState('')
  const [labelColor, setLabelColor] = React.useState<NoteColor>('default')
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const activeCount = notes.filter((note) => !note.archivedAt && !note.trashedAt).length
  const closeMobile = () => setOpenMobile(false)
  const openLabelDialog = (id: string, name: string, color: NoteColor = 'default') => { setLabelDialog(id); setLabelName(name); setLabelColor(color) }

  return <>
    <Sidebar collapsible="icon" className="app-sidebar" aria-label="Notes navigation">
      <SidebarHeader className="gap-0 px-2 pb-3 pt-3 group-data-[collapsible=icon]:px-0">
        <div className="flex h-14 items-center gap-3 px-3 text-[1.05rem] font-bold tracking-tight group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <span className="brand-mark shrink-0"><Pencil className="size-5" strokeWidth={2.4} /></span>
          <span className="group-data-[collapsible=icon]:hidden">Notes</span>
        </div>
      </SidebarHeader>
      <SidebarSeparator className="sidebar-divider" />
      <SidebarContent className="gap-3 px-2 py-3">
        <SidebarGroup className="p-0">
          <SidebarMenu className="gap-1">
            {mainLinks.map(({ to, label, icon: Icon }) => <SidebarMenuItem key={to}>
              <SidebarMenuButton asChild isActive={pathname === to} tooltip={label} className={navButtonClass}>
                <Link to={to} onClick={closeMobile}><Icon /><span>{label}</span></Link>
              </SidebarMenuButton>
              {to === '/' && <SidebarMenuBadge className="sidebar-count">{activeCount}</SidebarMenuBadge>}
            </SidebarMenuItem>)}
          </SidebarMenu>
        </SidebarGroup>

        <SidebarGroup className="p-0">
          <SidebarGroupLabel className="h-8 px-3 text-xs font-semibold">Labels</SidebarGroupLabel>
          <SidebarGroupAction className="sidebar-label-action" aria-label="Create label" title="Create label" onClick={() => openLabelDialog('new', '')}><Plus /></SidebarGroupAction>
          <SidebarMenu className="gap-1">
            {labels.map((label) => <SidebarMenuItem key={label.id}>
              <SidebarMenuButton asChild isActive={pathname === `/labels/${label.id}`} tooltip={label.name} className={navButtonClass}>
                <Link to="/labels/$labelId" params={{ labelId: label.id }} onClick={closeMobile}><span className="label-dot sidebar-label-dot" style={noteColorVars(label.color)} aria-hidden="true" /><span>{label.name}</span></Link>
              </SidebarMenuButton>
              <SidebarMenuAction showOnHover className="sidebar-row-action" aria-label={`Edit ${label.name} label`} title={`Edit ${label.name} label`} onClick={() => openLabelDialog(label.id, label.name, label.color)}><Pencil /></SidebarMenuAction>
            </SidebarMenuItem>)}
          </SidebarMenu>
        </SidebarGroup>

        <SidebarGroup className="p-0">
          <SidebarMenu className="gap-1">
            <SidebarMenuItem><SidebarMenuButton asChild isActive={pathname === '/archive'} tooltip="Archive" className={navButtonClass}><Link to="/archive" onClick={closeMobile}><Archive /><span>Archive</span></Link></SidebarMenuButton></SidebarMenuItem>
            <SidebarMenuItem><SidebarMenuButton asChild isActive={pathname === '/trash'} tooltip="Bin" className={navButtonClass}><Link to="/trash" onClick={closeMobile}><Trash2 /><span>Bin</span></Link></SidebarMenuButton></SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>

    <Dialog open={labelDialog !== null} onOpenChange={(open) => !open && setLabelDialog(null)}>
      <DialogContent className="sm:max-w-sm">
        <DialogTitle>{labelDialog === 'new' ? 'Create label' : 'Edit label'}</DialogTitle>
        <DialogDescription>Labels are shared with your family.</DialogDescription>
        <form className="grid gap-4" onSubmit={(event) => {
          event.preventDefault()
          const name = labelName.trim()
          if (!name) return
          if (labelDialog === 'new') addLabel(name, labelColor)
          else if (labelDialog) renameLabel(labelDialog, name, labelColor)
          setLabelDialog(null)
        }}>
          <Input maxLength={80} value={labelName} onChange={(event) => setLabelName(event.target.value)} autoFocus aria-label="Label name" />
          <fieldset className="grid gap-2">
            <legend className="mb-2 text-sm font-medium">Colour</legend>
            <ColourPicker value={labelColor} onChange={setLabelColor} />
          </fieldset>
          <div className="flex justify-end gap-2">
            {labelDialog !== 'new' && <Button type="button" variant="outline" size="icon" aria-label="Delete label" onClick={() => { if (labelDialog && confirm('Delete this family label? Notes will be kept.')) removeLabel(labelDialog); setLabelDialog(null) }}><Trash2 className="size-4" /></Button>}
            <Button size="icon" aria-label="Save label" disabled={!labelName.trim()}><Check className="size-4" /></Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  </>
}
